/**
 * @fileOverview Displays a combination of filter options and a search box
 * horizontally. Designed to be used on the filter table.
 */

import React, { useEffect, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import injectStyles from 'react-jss'

import ButtonGroup from '@material-ui/core/ButtonGroup'
import SubmitButton from 'Components/Form/SubmitButton'
import SearchBox from './SearchBox'
import { FilterSelect } from './../Filter/FilterSelect'
import './types'
import {
  urlGet,
  urlSet,
  urlRemove,
  urlRemoveExist,
  urlAddExist,
} from './../../Utils/URL'
import { isDefined } from '../../shared/libraries/utils/object'
import DateRangeSelector from '../DateRangeSelector'
import MonthSelector from '../MonthSelector'
import { splitTitle } from './utils'
import { styles } from './styles'

/**
 * @typedef {SearchAndFiltersProps}
 * @property {Array<Filter|MultiFilter>} filters The filter options, see the
 * types file.
 * @property {string} searchText The current search text
 * @property {Function} onSearchTextChange Fired when the search is triggerd.
 * i.e the user hits enter or clicks the search button. If you want finer
 * grained control on changing on keyPress use the `searchHandleInputChange`
 * method.
 * @property {string} className Override the root class name.
 * @property {string} searchInputClassName Override the search input class name.
 * @property {Function} searchHandleInputChange Fired on each key press / change
 * for the search input field, useful if you want to customise your own
 * auto complete options.
 * @property {boolean} enableFilterSuggestions If set to true, makes every
 * filter dropdown option something that can be searched for and tagged to
 * specify. Defaults to false. May want to use this in combination with
 * `enableMultiFilterValues`.
 * @property {boolean} enableMultiFilterValues Defaults to false. If set to
 * true you must provide filters of type MultiFilter. This will allow the user
 * to apply more than one option to a filter at a time. The gotcha with this
 * is that once enabled the filter `onChange` event's first parameter will
 * be an array and not a string.
 * @property {Array<string>} searchTags Add your own selected search tags.
 * @property {Array<string>} searchSuggestions Provide your own auto complete /
 * search suggestions.
 * @property {Function} searchOnRemoveTag If providing your own tags this will
 * fire when a tag is removed, the only parameter is the string tag.
 * @property {Function} searchOnAddTag If providing your own suggestions this
 * will fire when a suggestion is selected, the only parameter is the string
 * suggestion.
 *
 * @param {SearchAndFiltersProps} props
 */
const SearchAndFilters = props => {
  var {
    onSearchTextChange,
    searchText,
    filters = [],
    classes,
    className,
    searchInputClassName,
    searchHandleInputChange,
    enableFilterSuggestions,
    enableMultiFilterValues,
    searchTags,
    searchSuggestions,
    searchOnRemoveTag,
    searchOnAddTag,
    presets,
  } = props

  var showSearch = onSearchTextChange || searchText !== undefined
  const [filtersPreset, setFiltersPreset] = useState('No Preset')

  const dropPresets = () => {
    if (filtersPreset !== 'No Preset') {
      setFiltersPreset('No Preset')
    }
  }

  useEffect(
    () => {
      filters.forEach(({ label, onChange, onChangeMulti }) => {
        var urlValue = urlGet(label)
        if (urlValue) {
          const value = decodeURIComponent(urlValue)

          onChange(value)
          if (onChangeMulti)
            onChangeMulti(Array.isArray(value) ? value : [value])
        }
      })
      if (showSearch) {
        var value = urlGet('search')
        if (value) onSearchTextChange(value)
      }
    },
    // Run only once, as this is to sync on load only
    [],
  )

  var localTags = [],
    localSuggestions = []

  if (enableFilterSuggestions) {
    let suggestionsHash = {}
    filters.forEach(({ values }) => {
      values.forEach(
        v =>
          (suggestionsHash[typeof v === 'object' ? v.label : v] =
            typeof v === 'object'
              ? {
                  id: v.value,
                  name: v.label,
                  tag: v.tag,
                }
              : v),
      )
    })
    let filterTags = []
    filters.forEach(({ selectedValue, multiSelectedValue, label, values }) => {
      if (multiSelectedValue) {
        multiSelectedValue.forEach(
          v => delete suggestionsHash[typeof v === 'object' ? v.label : v],
        )
        if (values.find(value => typeof value === 'object')) {
          filterTags.push(
            values
              .filter(item => multiSelectedValue.includes(item.value))
              .map(({ value, label, tag }) => ({
                id: value,
                name: label,
                tag,
              })),
          )
        } else {
          filterTags.push(multiSelectedValue)
        }
      } else {
        if (!selectedValue) return undefined
        if (!(selectedValue instanceof Array)) selectedValue = [selectedValue]
        selectedValue.forEach(v => delete suggestionsHash[v])
        filterTags.push(selectedValue)
      }
    })

    filterTags = filterTags.flat()
    filterTags.forEach(t => {
      if (t) localTags.push(t)
    })
    var filterSuggestions = Object.keys(suggestionsHash)
    filterSuggestions.forEach(s => localSuggestions.push(suggestionsHash[s]))
  }

  const onAddTag = function(tag) {
    // Find the first matching filter option
    const handled = filters.find(
      ({
        label,
        values,
        onChange,
        onChangeMulti,
        selectedValue,
        multiSelectedValue,
      }) => {
        const valuesArray =
          typeof values[0] === 'object' ? values.map(v => v.value) : values
        if (valuesArray.includes(tag)) {
          if (multiSelectedValue) {
            if (!multiSelectedValue.includes(tag)) {
              const newMultiSelectedValues = [...multiSelectedValue, tag]
              onChangeMulti(newMultiSelectedValues)
              if (enableMultiFilterValues) {
                onChange(newMultiSelectedValues)
              } else {
                onChange(tag)
              }
              urlAddExist(label, tag)
            }
          } else {
            if (enableMultiFilterValues) {
              if (selectedValue == null) var selectedValues = []
              else var selectedValues = selectedValue.slice(0)
              selectedValues.push(tag)
              return onChange(selectedValues)
            } else {
              return onChange(tag)
            }
          }
          return true
        }
        return undefined
      },
    )
    if (!handled && searchOnAddTag) return searchOnAddTag(tag)
  }

  const onRemoveTag = function(tag) {
    dropPresets()
    var handled = filters.find(
      ({
        label,
        selectedValue,
        multiSelectedValue,
        onChange,
        onChangeMulti,
      }) => {
        if (multiSelectedValue) {
          if (multiSelectedValue.findIndex(v => v == tag) == -1)
            return undefined
          const newMultiSelectedValues = multiSelectedValue.filter(
            v => v != tag,
          )
          onChangeMulti(
            newMultiSelectedValues.length ? newMultiSelectedValues : [],
          )
          if (enableMultiFilterValues) {
            onChange(
              newMultiSelectedValues.length ? newMultiSelectedValues : null,
            )
          } else {
            onChange(
              newMultiSelectedValues.length
                ? newMultiSelectedValues.slice(-1)
                : null,
            )
          }

          if (newMultiSelectedValues.length == 0) urlRemove(label)
          else urlRemoveExist(label, tag)
          return true
        } else {
          if (enableMultiFilterValues) {
            if (!Array.isArray(selectedValue)) return undefined
            if (selectedValue.findIndex(v => v == tag) == -1) return undefined
            let newSelectedValues = selectedValue.filter(v => v != tag)
            onChange(newSelectedValues)
            if (newSelectedValues.length == 0) urlRemove(label)
            else urlRemoveExist(label, tag)
            return true
          } else {
            if (selectedValue != tag) return undefined
            onChange(null)
            urlRemove(label)
            return true
          }
        }
      },
    )
    if (!handled && searchOnRemoveTag) return searchOnRemoveTag(tag)
  }

  if (searchTags) {
    searchTags.forEach(t => {
      localTags.push(t)
    })
  }

  if (searchSuggestions) {
    searchSuggestions.forEach(s => localSuggestions.push(s))
  }

  return (
    <div
      className={classNames(
        {
          [classes.container]: true,
        },
        className,
        'SearchAndFilters',
      )}
    >
      {filters.length
        ? filters.map((filter, index) => {
            // We want to add an 'All' filter option
            var values = filter.values.slice(0)
            values.splice(0, 0, 'All')

            //var options = values.map(v => ({ label: v, value: v }))
            var options = values.map(v =>
              typeof v === 'object' ? v : { label: v, value: v },
            )
            // If the 'All' option is selected then return null as the selection
            var onChange = (e, i, v) => {
              dropPresets()
              v = v == 'All' ? null : v
              if (filter.multiSelectedValue) {
                // The current UI only allows you select one item at a time
                let newMultiSelectedValues = [...filter.multiSelectedValue]
                if (v == null) newMultiSelectedValues = []
                else if (!newMultiSelectedValues.includes(v)) {
                  newMultiSelectedValues.push(v)
                }
                filter.onChangeMulti(newMultiSelectedValues)
                if (filter.enableMultiFilterValues) {
                  return filter.onChange(newMultiSelectedValues)
                } else {
                  return filter.onChange(v)
                }
              } else {
                if (enableMultiFilterValues) {
                  // The current UI only allows you select one item at a time
                  return filter.onChange(v == null ? null : [v])
                } else {
                  return filter.onChange(v)
                }
              }
            }
            const onDateChange = value => {
              if (!value) {
                urlRemove(filter.label)
                return filter.onChange('')
              }
              const date = moment(value).format('YYYY-MM-DDTHH:mm:ssZ')
              urlSet(filter.label, date)
              return filter.onChange(date)
            }
            var selectedValue =
              filter.selectedValue == null ? 'All' : filter.selectedValue
            if (
              selectedValue instanceof Array &&
              selectedValue.length == 1 &&
              selectedValue[0] == null
            ) {
              throw new Error(
                "Don't pass [null], instead set selectedValue to null.",
              )
            }

            if (selectedValue instanceof Array && !filter.multiple) {
              if (selectedValue.length > 1 && !enableMultiFilterValues) {
                options = ['All', 'Multiple selected'].map(v => ({
                  label: v,
                  value: v,
                }))
                selectedValue = 'Multiple selected'
                var disabled = true
              } else {
                if (selectedValue.length)
                  selectedValue = selectedValue[selectedValue.length - 1]
                else selectedValue = 'All'
              }
            }
            const displayFilter = filter => {
              if (filter.isMonthFilter) {
                return (
                  <MonthSelector
                    title={filter.label}
                    onChange={value => {
                      onDateChange(
                        value === 'All' ? null : moment(value).endOf('month'),
                      )
                      dropPresets()
                    }}
                    value={selectedValue}
                    disabled={disabled}
                  />
                )
              } else {
                return (
                  <FilterSelect
                    label={filter.label}
                    onChange={onChange}
                    options={options}
                    value={selectedValue}
                    disabled={disabled}
                    sorted={isDefined(filter.sorted) ? filter.sorted : true}
                    withoutLabel={filter.withoutLabel}
                    multiple={filter.multiple}
                    listSubheaders={filter.listSubheaders}
                    cypress_id="campaigns-page-filter"
                    enableMultiFilterValues={enableMultiFilterValues}
                  />
                )
              }
            }
            if (filter.label != 'startDate' && filter.label != 'endDate') {
              return (
                <div
                  key={`${filter.label}-${index}`}
                  className="SearchAndFilters-filterGroup"
                >
                  {displayFilter(filter)}
                </div>
              )
            }
            return (
              <div className="SearchAndFilters-filterGroup">
                <DateRangeSelector
                  title={splitTitle(filter.label)}
                  value={
                    selectedValue && selectedValue !== 'All'
                      ? moment(selectedValue)
                      : ''
                  }
                  onChange={value => {
                    onDateChange(value)
                    dropPresets()
                  }}
                />
              </div>
            )
          })
        : null}
      {showSearch ? (
        <div className={classes.searchContainer}>
          <SearchBox
            inputClassName={classNames(classes.searchBox, searchInputClassName)}
            onChange={str => {
              if (str) {
                setFiltersPreset('No Preset')
              }
              // Label could conflict with a filter, but seems unlikely
              else urlRemove('search')
              return onSearchTextChange(str)
            }}
            value={searchText}
            handleInputChange={searchHandleInputChange}
            suggestions={localSuggestions}
            tags={localTags}
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
          />
          {presets && presets.length
            ? presets.map(preset => (
                <div className={classes.presets}>
                  <ButtonGroup
                    disableElevation
                    color="primary"
                    className={classes.select}
                  >
                    {preset.items.map(item => (
                      <SubmitButton
                        onClick={() => {
                          return (
                            setFiltersPreset(item.label), item.action(filters)
                          )
                        }}
                        value={item.label}
                        variant={
                          filtersPreset === item.label
                            ? 'contained'
                            : 'outlined'
                        }
                      />
                    ))}
                  </ButtonGroup>
                </div>
              ))
            : null}
        </div>
      ) : null}
    </div>
  )
}

const StyledSearchAndFilters = injectStyles(styles)(SearchAndFilters)

export default StyledSearchAndFilters
